import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdArrowForward,
  MdAssessment,
  MdAutoGraph,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdDashboard,
  MdFactory,
  MdGroups,
  MdHistory,
  MdLockOutline,
  MdOutlineInventory2,
  MdPlayArrow,
  MdTaskAlt,
  MdTrendingUp,
  MdWhatsapp,
} from 'react-icons/md';
import './style.css';
import dashboardScreenshot from '../../../assets/carrossel/DASHBOARD.jpg';
import messengerScreenshot from '../../../assets/carrossel/MESSENGER.jpg';
import pcpScreenshot from '../../../assets/carrossel/PCP.jpg';
import pipelineScreenshot from '../../../assets/carrossel/PIPELINE.jpg';
import cardScreenshot from '../../../assets/carrossel/CARD.jpg';

const productSlides = [
  {
    image: pipelineScreenshot,
    title: 'Pipeline comercial visual',
    description: 'Acompanhe cada oportunidade, responsável e etapa da venda em uma visão simples.',
  },
  {
    image: messengerScreenshot,
    title: 'Atendimento conectado ao CRM',
    description: 'Converse com clientes pelo WhatsApp Business sem perder o contexto comercial.',
  },
  {
    image: dashboardScreenshot,
    title: 'Indicadores para decidir melhor',
    description: 'Transforme a rotina da equipe em informações claras para acompanhar resultados.',
  },
  {
    image: pcpScreenshot,
    title: 'Produção acompanhada de perto',
    description: 'Organize pedidos e etapas produtivas para que comercial e operação trabalhem juntos.',
  },
  {
    image: cardScreenshot,
    title: 'Histórico completo de cada negócio',
    description: 'Centralize informações, tarefas, arquivos e movimentações importantes em um só lugar.',
  },
];

const features = [
  {
    icon: MdTrendingUp,
    title: 'CRM e vendas',
    description: 'Organize oportunidades em etapas, distribua responsáveis e acompanhe cada negociação até o fechamento.',
  },
  {
    icon: MdWhatsapp,
    title: 'WhatsApp Oficial',
    description: 'Atenda pelo WhatsApp Business dentro do CRM, com conversas, mídias e contexto do cliente reunidos.',
  },
  {
    icon: MdTaskAlt,
    title: 'Tarefas e rotina',
    description: 'Registre próximos passos, mantenha os combinados visíveis e ajude a equipe a não perder prazos.',
  },
  {
    icon: MdHistory,
    title: 'Histórico centralizado',
    description: 'Consulte atividades e informações importantes sem depender de planilhas ou anotações espalhadas.',
  },
  {
    icon: MdAssessment,
    title: 'Dashboards',
    description: 'Visualize o desempenho comercial e operacional para identificar prioridades e oportunidades.',
  },
  {
    icon: MdFactory,
    title: 'PCP e produção',
    description: 'Acompanhe o andamento dos pedidos e conecte as etapas comerciais à execução da produção.',
  },
  {
    icon: MdGroups,
    title: 'Equipe e participantes',
    description: 'Mantenha clientes, contatos, responsáveis e permissões organizados conforme a rotina da empresa.',
  },
  {
    icon: MdOutlineInventory2,
    title: 'Operação integrada',
    description: 'Reúna projetos, documentos, importações e painéis de acompanhamento em um único ambiente.',
  },
];

const journey = [
  ['01', 'Organize', 'Centralize contatos, oportunidades e informações importantes.'],
  ['02', 'Acompanhe', 'Visualize a etapa atual e o próximo passo de cada negociação.'],
  ['03', 'Converse', 'Atenda o cliente pelo canal oficial sem perder o histórico.'],
  ['04', 'Produza', 'Conecte a venda às etapas operacionais e acompanhe a execução.'],
  ['05', 'Evolua', 'Use indicadores para ajustar a operação e melhorar os resultados.'],
];

function LandingPage() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % productSlides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  const changeSlide = (direction) => {
    setCurrentIndex((previous) => (
      previous + direction + productSlides.length
    ) % productSlides.length);
  };

  const goToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="syncs-landing">
      <header className="syncs-landing-header">
        <button className="syncs-landing-brand" type="button" onClick={() => goToSection('inicio')} aria-label="Ir para o início">
          <img src="/logo192.png" alt="" />
          <span>Syncs<span>CRM</span></span>
        </button>

        <nav className="syncs-landing-nav" aria-label="Navegação principal">
          <button type="button" onClick={() => goToSection('recursos')}>Recursos</button>
          <button type="button" onClick={() => goToSection('como-funciona')}>Como funciona</button>
          <button type="button" onClick={() => goToSection('plataforma')}>Plataforma</button>
        </nav>

        <button className="syncs-landing-login" type="button" onClick={() => navigate('/login')}>
          Acessar plataforma <MdArrowForward aria-hidden="true" />
        </button>
      </header>

      <main>
        <section className="syncs-landing-hero" id="inicio">
          <div className="syncs-landing-glow syncs-landing-glow-one" />
          <div className="syncs-landing-glow syncs-landing-glow-two" />
          <div className="syncs-landing-hero-copy">
            <span className="syncs-landing-eyebrow"><MdAutoGraph /> Gestão comercial e operacional</span>
            <h1>Menos tarefas espalhadas.<br /><span>Mais clareza para crescer.</span></h1>
            <p>
              O SyncsCRM conecta vendas, atendimento, equipe e produção para sua empresa acompanhar
              toda a jornada do cliente em um só lugar.
            </p>
            <div className="syncs-landing-hero-actions">
              <button className="syncs-landing-primary" type="button" onClick={() => navigate('/login')}>
                Entrar no SyncsCRM <MdArrowForward />
              </button>
              <button className="syncs-landing-secondary" type="button" onClick={() => goToSection('plataforma')}>
                <MdPlayArrow /> Conhecer a plataforma
              </button>
            </div>
            <div className="syncs-landing-proof" aria-label="Principais benefícios">
              <span><MdCheckCircle /> Visão completa do negócio</span>
              <span><MdCheckCircle /> Atendimento conectado</span>
              <span><MdCheckCircle /> Rotina organizada</span>
            </div>
          </div>

          <div className="syncs-landing-hero-visual" aria-label="Visão do pipeline comercial do SyncsCRM">
            <div className="syncs-landing-visual-frame">
              <div className="syncs-landing-window-bar">
                <span /><span /><span />
                <b>Visão comercial</b>
              </div>
              <img src={pipelineScreenshot} alt="Pipeline comercial organizado em etapas no SyncsCRM" />
            </div>
            <div className="syncs-landing-float-card syncs-landing-float-card-one">
              <MdWhatsapp /><span><b>Atendimento</b> conectado ao cliente</span>
            </div>
            <div className="syncs-landing-float-card syncs-landing-float-card-two">
              <MdDashboard /><span><b>Indicadores</b> sempre visíveis</span>
            </div>
          </div>
        </section>

        <section className="syncs-landing-section syncs-landing-value-strip" aria-label="Resumo da plataforma">
          <div><strong>Uma plataforma</strong><span>para integrar sua operação</span></div>
          <div><strong>Da prospecção ao pós-venda</strong><span>com histórico e responsáveis</span></div>
          <div><strong>Decisões mais claras</strong><span>com dados organizados</span></div>
        </section>

        <section className="syncs-landing-section syncs-landing-features" id="recursos">
          <div className="syncs-landing-section-heading">
            <span className="syncs-landing-kicker">Tudo conectado</span>
            <h2>O que sua equipe precisa para trabalhar melhor</h2>
            <p>Recursos pensados para simplificar a rotina, dar visibilidade aos processos e manter todos na mesma direção.</p>
          </div>
          <div className="syncs-landing-feature-grid">
            {features.map(({ icon: Icon, title, description }) => (
              <article className="syncs-landing-feature-card" key={title}>
                <span className="syncs-landing-feature-icon"><Icon aria-hidden="true" /></span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="syncs-landing-section syncs-landing-journey" id="como-funciona">
          <div className="syncs-landing-section-heading syncs-landing-section-heading-left">
            <span className="syncs-landing-kicker">Uma jornada contínua</span>
            <h2>Do primeiro contato à entrega</h2>
            <p>O trabalho avança sem perder informações pelo caminho.</p>
          </div>
          <div className="syncs-landing-journey-list">
            {journey.map(([number, title, description]) => (
              <article key={number}>
                <span>{number}</span>
                <div><h3>{title}</h3><p>{description}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section className="syncs-landing-section syncs-landing-showcase" id="plataforma">
          <div className="syncs-landing-section-heading">
            <span className="syncs-landing-kicker">Veja o SyncsCRM em ação</span>
            <h2>Uma visão simples para uma operação completa</h2>
          </div>
          <div className="syncs-landing-showcase-shell">
            <div className="syncs-landing-showcase-copy">
              <span>{String(currentIndex + 1).padStart(2, '0')} / {String(productSlides.length).padStart(2, '0')}</span>
              <h3>{productSlides[currentIndex].title}</h3>
              <p>{productSlides[currentIndex].description}</p>
              <div className="syncs-landing-carousel-controls">
                <button type="button" onClick={() => changeSlide(-1)} aria-label="Visualização anterior"><MdChevronLeft /></button>
                <button type="button" onClick={() => changeSlide(1)} aria-label="Próxima visualização"><MdChevronRight /></button>
              </div>
            </div>
            <div className="syncs-landing-showcase-image">
              <img src={productSlides[currentIndex].image} alt={productSlides[currentIndex].title} />
            </div>
          </div>
          <div className="syncs-landing-carousel-dots" aria-label="Selecionar visualização">
            {productSlides.map((slide, index) => (
              <button
                key={slide.title}
                type="button"
                className={index === currentIndex ? 'is-active' : ''}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Exibir ${slide.title}`}
                aria-current={index === currentIndex ? 'true' : undefined}
              />
            ))}
          </div>
        </section>

        <section className="syncs-landing-section syncs-landing-confidence">
          <div className="syncs-landing-confidence-icon"><MdLockOutline /></div>
          <div>
            <span className="syncs-landing-kicker">Controle para a empresa</span>
            <h2>Informação organizada e acesso de acordo com cada função</h2>
            <p>
              Centralize a operação, defina quem pode acessar cada área e mantenha o histórico necessário
              para acompanhar clientes e processos com mais segurança.
            </p>
          </div>
          <ul>
            <li><MdCheckCircle /> Acesso individual por usuário</li>
            <li><MdCheckCircle /> Permissões conforme o perfil</li>
            <li><MdCheckCircle /> Histórico das atividades</li>
          </ul>
        </section>

        <section className="syncs-landing-section syncs-landing-cta">
          <img src="/logo192.png" alt="Logotipo SyncsCRM" />
          <span className="syncs-landing-kicker">Sua operação, no mesmo ritmo</span>
          <h2>Entre no SyncsCRM e continue seu trabalho</h2>
          <p>Acesse sua conta para acompanhar vendas, atendimento e operação.</p>
          <button className="syncs-landing-primary" type="button" onClick={() => navigate('/login')}>
            Acessar minha conta <MdArrowForward />
          </button>
        </section>
      </main>

      <footer className="syncs-landing-footer">
        <div className="syncs-landing-footer-brand">
          <img src="/logo192.png" alt="" />
          <div><strong>SyncsCRM</strong><span>Gestão que conecta sua empresa.</span></div>
        </div>
        <div className="syncs-landing-footer-links">
          <a href="/politica-de-privacidade.html">Política de Privacidade</a>
          <a href="/exclusao-de-dados.html">Exclusão de Dados</a>
          <button type="button" onClick={() => navigate('/login')}>Login</button>
        </div>
        <p>© 2022 SyncsCRM. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
}

export default LandingPage;
